/**
 * ShiptimizeUI — all checkout UI manipulation in one place.
 *
 * No state. No AJAX. Pure DOM reads and writes.
 * Every method is named after what it does to the UI, not why.
 */
export default class ShiptimizeUI {
  // Shared selector for the place order button across block and classic checkout.
  static BTN = '.wc-block-components-checkout-place-order-button, #place_order';

  // ── Place order button ──────────────────────────────────────────────────────

  disableButton() {
    jQuery(ShiptimizeUI.BTN)
      .prop('disabled', true)
      .attr('disabled', 'disabled')
      .attr('aria-disabled', 'true')
      .addClass('shiptimize-disabled')
      .css({
        'pointer-events':   'none',
        cursor:             'not-allowed',
        opacity:            '0.5',
        'background-color': 'grey',
      });
  }

  enableButton() {
    jQuery(ShiptimizeUI.BTN)
      .prop('disabled', false)
      .removeAttr('disabled')
      .removeAttr('aria-disabled')
      .removeClass('shiptimize-disabled')
      .css({
        'pointer-events':   'auto',
        cursor:             'pointer',
        opacity:            '1',
        'background-color': '',
      });
  }

  // ── Pickup buttons ──────────────────────────────────────────────────────────

  removeAllPickupButtons() {
    document.querySelectorAll('.shiptimize-pick-location').forEach((btn) => btn.remove());
  }

  // ── Pickup display ──────────────────────────────────────────────────────────

  /**
   * Remove all pickup point display elements.
   * Called when a pickup is cleared. Does NOT touch hidden form fields —
   * ShiptimizeState is the only writer to those.
   */
  clearPickupDisplay() {
    jQuery('.pickup-label-display, .selected-pickup-point, .pickup-point-display, .selected-pickup-info').remove();
    jQuery('.shiptimize-pickup__description').html('').hide();
  }

  /**
   * Show the selected pickup label next to each pickup button.
   *
   * @param {string} label Human-readable pickup point name + address
   */
  showPickupLabel(label, scope = document) {
    const root = scope instanceof Element ? scope : document;
    root.querySelectorAll('.shiptimize-pick-location').forEach((button) => {
      const existing = button.parentNode.querySelector('.pickup-label-display');
      if (existing) existing.remove();

      const display = document.createElement('div');
      display.className = 'pickup-label-display';
      display.style.cssText = 'margin-top:10px;padding:8px;background:#f0f0f0;border-radius:4px;font-size:14px;color:#333;';
      display.innerHTML = `<strong>Selected Pickup Location:</strong><br>${label}`;
      button.parentNode.insertBefore(display, button.nextSibling);
    });

    jQuery(root).find('.shiptimize-pickup__description').show().html(label);
  }

  // ── Toast notifications ─────────────────────────────────────────────────────

  /**
   * Show a short-lived notification at the bottom-right of the screen.
   *
   * @param {string} message  HTML allowed
   * @param {number} duration Milliseconds before the toast fades out
   */
  showToast(message, duration = 4000) {
    jQuery('.shiptimize-toast').remove();

    const toast = jQuery('<div>', {
      html:  message,
      class: 'shiptimize-toast',
      css: {
        position:         'fixed',
        bottom:           '40px',
        right:            '40px',
        background:       '#111',
        color:            '#fff',
        padding:          '14px 20px',
        'border-radius':  '6px',
        'font-size':      '15px',
        'box-shadow':     '0 3px 10px rgba(0,0,0,0.3)',
        'z-index':        '99999',
        opacity:          '0',
        transition:       'opacity 0.3s ease',
      },
    });

    jQuery('body').append(toast);
    setTimeout(() => toast.css('opacity', '1'), 50);
    setTimeout(() => toast.fadeOut(400, () => toast.remove()), duration);
  }

  dismissToast() {
    jQuery('.shiptimize-toast').remove();
  }

  /**
   * Show the mandatory pickup toast once per carrier — suppresses repeats
   * so the customer isn't spammed when the UI re-evaluates.
   *
   * @param {number|string} carrierId  Current carrier ID
   */
  showMandatoryToastOnce(carrierId) {
    if (this._lastMandatoryToastCarrierId === carrierId) return;
    this._lastMandatoryToastCarrierId = carrierId;
    this.showToast(
      '<strong>Pickup required:</strong> Please select a pickup location to complete checkout.',
    );
  }

  resetMandatoryToast() {
    this._lastMandatoryToastCarrierId = null;
  }

  // ── Modals ──────────────────────────────────────────────────────────────────

  /**
   * Create and show a modal dialog.
   *
   * @param {object} config
   * @param {string} config.id
   * @param {string} config.message
   * @param {Array}  config.buttons  [{ props: {}, handler: fn }]
   * @returns {{ modal: jQuery, closeModal: function }}
   */
  createModal(config) {
    const modal = jQuery('<div>', {
      id:  config.id,
      css: {
        display:          'block',
        position:         'fixed',
        'z-index':        '9999',
        left:             '0',
        top:              '0',
        width:            '100%',
        height:           '100%',
        overflow:         'auto',
        'background-color': 'rgba(0,0,0,0.4)',
      },
    });

    const content = jQuery('<div>', {
      css: {
        'background-color': '#fefefe',
        margin:             '15% auto',
        padding:            '20px',
        border:             '1px solid #888',
        width:              '80%',
        'max-width':        '500px',
        'border-radius':    '5px',
        'text-align':       'center',
      },
    });

    const closeBtn = jQuery('<span>', {
      text: '×',
      css:  { color: '#aaa', float: 'right', 'font-size': '28px', 'font-weight': 'bold', cursor: 'pointer' },
    });

    content.append(closeBtn);
    content.append(jQuery('<p>', {
      text: config.message,
      css:  { 'font-size': '16px', margin: '20px 0', color: '#333' },
    }));

    (config.buttons || []).forEach((btnCfg) => {
      const btn = jQuery('<button>', btnCfg.props);
      if (btnCfg.handler) btn.on('click', btnCfg.handler);
      content.append(btn);
    });

    modal.append(content);
    jQuery('body').append(modal);

    const closeModal = () => modal.remove();
    closeBtn.on('click', closeModal);
    jQuery(window).on('click', (e) => { if (jQuery(e.target).is(modal)) closeModal(); });

    return { modal, closeModal };
  }

  /**
   * Show the "pickup is mandatory" modal with a button to open the map.
   *
   * @param {function} onSelectPickup  Called when the customer clicks "Choose Pickup Location"
   */
  showPickupMandatoryModal(onSelectPickup) {
    const { closeModal } = this.createModal({
      id:      'pickupMandatoryModal',
      message: typeof shiptimize_mandatory_point !== 'undefined'
        ? shiptimize_mandatory_point
        : 'Please select a pickup location to complete your order.',
      buttons: [
        {
          props:   { text: window.shiptimize_choose_pickup_location || 'Choose Pickup Location', class: 'button alt shiptimize-pick-location' },
          handler: (e) => { closeModal(); e.preventDefault(); onSelectPickup(); },
        },
        {
          props:   { text: 'OK', css: { 'background-color': '#666', color: 'white', border: 'none', padding: '6px 10px', 'border-radius': '4px', cursor: 'pointer', 'margin-left': '10px' } },
          handler: closeModal,
        },
      ],
    });
  }

  /**
   * Show the "address required" modal when the customer tries to open the map
   * without filling in their address first.
   */
  showAddressRequiredModal() {
    this.createModal({
      id:      'addressModal',
      message: 'Please fill in all required address fields before selecting a pickup location.',
      buttons: [{
        props:   { text: 'OK', css: { 'background-color': '#666', color: 'white', border: 'none', padding: '6px 10px', 'border-radius': '4px', cursor: 'pointer' } },
        handler: () => jQuery('#addressModal').remove(),
      }],
    });
  }
}
